import React from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";

interface DMHealthMethodologyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DMHealthMethodologyModal: React.FC<DMHealthMethodologyModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4`}
      onClick={onClose}
    >
      {/* anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div className={`${closing ? "anim-modal-out" : "anim-modal-in"} max-w-3xl w-full`}>
        <div
          className="detail-overlay rounded-lg shadow-xl w-full max-h-[80vh] overflow-y-auto"
          style={{ overscrollBehavior: "contain" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white/95 border-b border-black/[0.12] px-6 py-4 flex items-center justify-between">
            <h2 className="token-18 text-glass">How These Numbers Are Calculated</h2>
            <button
              onClick={onClose}
              className="anim-tap text-glass-muted hover:text-glass-secondary text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 token-14">
              <div>
                <h4 className="font-medium text-glass mb-2">
                  "New Chats" vs "Conversations Created"
                </h4>
                <ul className="list-disc list-inside text-glass-secondary space-y-1">
                  <li>
                    <strong>Conversations Created</strong> = any conversation whose first message is
                    on this date — lead-first, setter-first, AND ManyChat (mostly bot greets).
                  </li>
                  <li>
                    <strong>New Chats</strong> = the subset where the lead actually replied and a
                    setter was assigned — the real new leads. This is the headline number.
                  </li>
                  <li>One unique conversation per contact.</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-glass mb-2">ManyChat vs Manual Outbound</h4>
                <ul className="list-disc list-inside text-glass-secondary space-y-1">
                  <li>
                    <strong>ManyChat</strong>: Auto-messages to new followers
                  </li>
                  <li>
                    <strong>Manual</strong>: Setter personally reached out
                  </li>
                  <li>Both are "outbound" - setter sent first</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-glass mb-2">Excluded from counts:</h4>
                <ul className="list-disc list-inside text-glass-secondary space-y-1">
                  <li>"Opportunity created/updated" system logs</li>
                  <li>"Contact created" system entries</li>
                  <li>Call logs and internal comments</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-glass mb-2">Key Metrics:</h4>
                <ul className="list-disc list-inside text-glass-secondary space-y-1">
                  <li>
                    <strong>Conversations</strong> = 4+ setter reply turns (deep engagement)
                  </li>
                  <li>
                    <strong>Replied</strong> = a human setter sent at least 1 message. Lead reply
                    not required — this measures setter effort, not lead engagement. ManyChat does
                    not count (automated messages with NULL ghl_sender_user_id are excluded from all
                    setter-activity rate metrics on this dashboard). The two-way measure (2 lead + 2
                    setter replies) is the separate <strong>Engaged</strong> tile in the Inbound
                    Funnel.
                  </li>
                  <li>
                    <strong>Follow-ups</strong> = setter messaged on 2+ different days
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
